"use client";

import { createBrowserClient } from "@supabase/ssr";
import { SessionContextProvider } from "@supabase/auth-helpers-react";
import { useState } from "react";

interface AuthKitWrapperProps {
  children: React.ReactNode;
}

function getCookieDomain() {
  if (typeof window === "undefined") {
    return undefined; // No domain on server-side
  }
  const hostname = window.location.hostname;
  if (hostname === "localhost") {
    return undefined; // No domain for localhost
  }
  return `.${hostname.replace(/^.*?([^.]+\.[^.]+)$/, "$1")}`;
}

/**
 * Delete old Supabase cookies that were set on exact domains (e.g. www.symbolica.ai)
 * rather than the wildcard domain (.symbolica.ai). Having both causes auth issues.
 */
function clearOldCookies() {
  if (typeof window === "undefined") return;
  const hostname = window.location.hostname;
  if (hostname === "localhost" || !hostname.includes("symbolica")) return;

  const cookieNames = document.cookie
    .split(";")
    .map((c) => c.trim().split("=")[0])
    .filter(Boolean);

  for (const name of cookieNames) {
    // Try both: host-only cookies (no domain attr) and domain cookies (explicit domain).
    // Only one will match the old cookie, but both are safe to send.
    document.cookie = `${name}=; Max-Age=0; path=/`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=${hostname}`;
  }
}

export function AuthKitWrapper({ children }: AuthKitWrapperProps) {
  const [supabaseClient] = useState(() => {
    clearOldCookies();
    const domain = getCookieDomain();
    console.log("Supabase client initialized with domain:", domain);
    return createBrowserClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      domain ? { cookieOptions: { domain } } : undefined
    );
  });

  return (
    <SessionContextProvider supabaseClient={supabaseClient}>
      {children}
    </SessionContextProvider>
  );
}
